/**
 * The body of an app tile: a box the app draws from its own data
 * (useLookerAppTiles), picked by `data.widget`. Each box fails on its own.
 *
 * @module components/looker/app/AppTileBody
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AppTileState } from '@/hooks/useLookerAppTiles';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import type { AppTileData } from '@/services/looker/lookerAppData';
import { BoxError, BoxMessage } from './appBoxShared';
import { OutcomesBox } from './OutcomesBox';
import { AttentionBox } from './AttentionBox';
import { DataQualityBox } from './DataQualityBox';
import { BudgetPacingBox } from './BudgetPacingBox';

/** The same pulsing block LookerTileImage shows while Looker draws. */
const Pending: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex h-32 animate-pulse items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-700">
      {t('looker.app.loading')}
    </div>
  );
};

const WidgetView: React.FC<{ data: AppTileData; coverage?: CoverageState }> = ({ data, coverage }) => {
  switch (data.widget) {
    case 'outcomes':
      return <OutcomesBox outcomes={data.outcomes} costPerLead={data.costPerLead} mediaRoas={data.mediaRoas} money={data.money} />;
    case 'attention':
      return <AttentionBox events={data.events} windowDays={data.windowDays} truncated={data.truncated} />;
    case 'dataQuality':
      return <DataQualityBox actions={data.actions} coverage={coverage} />;
    case 'budgetPacing':
      return <BudgetPacingBox view={data.view} />;
  }
};

interface Props {
  state?: AppTileState;
  /** The page has a channel filter on. App boxes never apply it, so a ready box says it covers all channels. */
  channelFiltered?: boolean;
  /** The page's coverage state, drawn in box 8. Absent = no coverage section. */
  coverage?: CoverageState;
}

export const AppTileBody: React.FC<Props> = ({ state, channelFiltered = false, coverage }) => {
  const { t } = useTranslation();
  if (!state || state.status === 'loading') return <Pending />;
  if (state.status === 'failed') return <BoxError text={t('looker.app.failed')} />;
  if (state.status === 'needsWorkspace') return <BoxMessage text={t('looker.app.needsWorkspace')} />;
  return (
    <>
      {/* Box 6 states in its own header that it ignores the filters. */}
      {channelFiltered && state.data.widget !== 'budgetPacing' && <p className="mb-2 text-xs text-neutral-700">{t('looker.app.allChannels')}</p>}
      <WidgetView data={state.data} coverage={coverage} />
    </>
  );
};
